import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Input } from "antd";
import { propertyService, googleService } from "@iso/services";
import { SETUP_VARIANT } from "@iso/constants/onboarding";

const findAddressComponent = (components, type) =>
  components.find((component) => component.types[0] === type) || {};

// Contractors and ag services businesses get a property named after their
// company. Properties are created private (is_public is not used anywhere
// yet, so onboarding doesn't ask); people join with the property code or an
// invitation either way.
const COPY = {
  [SETUP_VARIANT.FARM]: {
    title: "Tell us about your property",
    lead:
      "Your property is where you build your map, manage safety assets, livestock, machinery, crops and staff.",
    nameLabel: "Property name",
    namePlaceholder: "e.g. Green Valley Station",
    nameRequired: "Please enter a property name",
    addressLabel: "Property address",
    addressRequired: "Please enter the property address",
  },
  [SETUP_VARIANT.BUSINESS]: {
    title: "Tell us about your business",
    lead: "We'll set AirAgri up for your company using these details.",
    nameLabel: "Company name",
    namePlaceholder: "e.g. Outback Ag Services Pty Ltd",
    nameRequired: "Please enter your company name",
    addressLabel: "Business address",
    addressRequired: "Please enter your business address",
  },
};

const PropertyStep = ({ variant = SETUP_VARIANT.FARM, purpose, propertyId, initialValues, onSaved }) => {
  const copy = COPY[variant] || COPY[SETUP_VARIANT.FARM];
  const isBusiness = variant === SETUP_VARIANT.BUSINESS;
  const [form] = Form.useForm();
  const addressRef = useRef(null);
  const [errMsg, setErrMsg] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const input = addressRef.current && addressRef.current.input;
    const maps = window.google && window.google.maps;
    if (!input || !maps || !maps.places) {
      return undefined;
    }
    const autoComplete = new maps.places.Autocomplete(input, {
      types: ["address"],
    });
    autoComplete.setFields(["formatted_address"]);
    autoComplete.addListener("place_changed", () => {
      const place = autoComplete.getPlace();
      if (place && place.formatted_address) {
        form.setFieldsValue({ address: place.formatted_address });
      }
    });
    return () => maps.event.clearInstanceListeners(autoComplete);
  }, [form]);

  const fail = (message) => {
    setErrMsg(message);
    setLoading(false);
  };

  const handleFinish = async (values) => {
    setErrMsg("");
    setLoading(true);
    try {
      const geocode = await googleService.getLatLng(
        encodeURIComponent(values.address)
      );
      const results = (geocode && geocode.results) || [];
      const result = results[results.length - 1];
      if (!result || !result.geometry) {
        fail(
          "We couldn't find that address. Pick a suggestion from the list or check the spelling."
        );
        return;
      }

      const components = result.address_components || [];
      const country = findAddressComponent(components, "country");
      const state = findAddressComponent(
        components,
        "administrative_area_level_1"
      );
      if (!country.long_name || !state.long_name) {
        fail("Please enter a full address, including the state and country.");
        return;
      }

      const data = {
        property: {
          ...values,
          address: result.formatted_address,
          is_public: 0,
          point: {
            lat: result.geometry.location.lat,
            lng: result.geometry.location.lng,
          },
          country: country.long_name,
          state: state.long_name,
          acreage_unit: "acres",
          use_my_account: false,
          purpose,
        },
        addressShortName: {
          country: country.short_name || "",
          state: state.short_name || "",
        },
      };

      const res = await propertyService.storeOrUpdateProperties(
        data,
        propertyId
      );
      if (res && res.code === "0000" && res.property && res.property.id) {
        setLoading(false);
        localStorage.setItem("active_property", JSON.stringify(res.property));
        onSaved(res.property, values);
      } else {
        fail("We couldn't save your property. Please try again.");
      }
    } catch (e) {
      fail("We couldn't save your property. Please check your connection and try again.");
    }
  };

  return (
    <>
      <h1 className="ob-title" tabIndex={-1}>
        {copy.title}
      </h1>
      <p className="ob-lead">{copy.lead}</p>
      {errMsg && (
        <div className="ob-error" role="alert">
          {errMsg}
        </div>
      )}
      <Form
        // A named form gives the inputs unique ids ("onboarding_property_name")
        // instead of a bare "name", which browsers treat as a login field and
        // fill with the saved account email.
        name="onboarding_property"
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onFinish={handleFinish}
      >
        <Form.Item
          name="name"
          label={copy.nameLabel}
          rules={[
            {
              required: true,
              whitespace: true,
              message: copy.nameRequired,
            },
          ]}
        >
          <Input
            placeholder={copy.namePlaceholder}
            autoComplete={isBusiness ? "organization" : "off"}
          />
        </Form.Item>
        <Form.Item
          name="address"
          label={copy.addressLabel}
          rules={[
            {
              required: true,
              whitespace: true,
              message: copy.addressRequired,
            },
          ]}
        >
          <Input
            ref={addressRef}
            placeholder="Start typing and pick a suggestion"
            // Enter picks a Places suggestion; don't let it submit the form.
            onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
          />
        </Form.Item>
        <div className="ob-actions">
          <Button className="ob-btn-primary" htmlType="submit" loading={loading}>
            Continue
          </Button>
        </div>
      </Form>
    </>
  );
};

export default PropertyStep;
